import { stripLocaleFromPathname } from './locales';

export const pageRegistry = {
  home: {
    path: '/',
    seo: 'required',
    cta: 'required',
    content: 'required',
  },
  about: {
    path: '/about',
    seo: 'required',
    cta: 'required',
    content: 'required',
  },
  collections: {
    path: '/collections',
    seo: 'required',
    cta: 'required',
    content: 'partial',
  },
  contact: {
    path: '/contact',
    seo: 'required',
    cta: 'required',
    content: 'required',
  },
  journey: {
    path: '/journey',
    seo: 'required',
    cta: 'required',
    content: 'required',
  },
  'wedding-gifting': {
    path: '/wedding-gifting',
    seo: 'required',
    cta: 'required',
    content: 'required',
  },
} as const;

export type MarketingPageId = keyof typeof pageRegistry;
export type PageRequirement = 'required' | 'optional' | 'partial' | 'none';

type Registry = typeof pageRegistry;
type RequiredPageId<Key extends keyof Registry[MarketingPageId]> = {
  [PageId in MarketingPageId]: Registry[PageId][Key] extends 'required' ? PageId : never;
}[MarketingPageId];

export type SeoRequiredPageId = RequiredPageId<'seo'>;
export type CtaRequiredPageId = RequiredPageId<'cta'>;
export type ContentRequiredPageId = RequiredPageId<'content'>;

export const marketingPageIds = Object.keys(pageRegistry) as MarketingPageId[];
export const seoRequiredPageIds = marketingPageIds.filter(
  (pageId): pageId is SeoRequiredPageId => pageRegistry[pageId].seo === 'required'
);
export const ctaRequiredPageIds = marketingPageIds.filter(
  (pageId): pageId is CtaRequiredPageId => pageRegistry[pageId].cta === 'required'
);
export const contentRequiredPageIds = marketingPageIds.filter(
  (pageId): pageId is ContentRequiredPageId => pageRegistry[pageId].content === 'required'
);

function normalizePathname(pathname: string) {
  const [rawPath = '/'] = pathname.split(/[?#]/);
  const path = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;

  if (path === '/') {
    return path;
  }

  return path.replace(/\/+$/, '');
}

export function resolveMarketingPageId(pathname: string): MarketingPageId | null {
  const normalizedPath = normalizePathname(stripLocaleFromPathname(pathname));
  const page = marketingPageIds.find((pageId) => pageRegistry[pageId].path === normalizedPath);

  return page ?? null;
}
